// Section 22 right panel (always visible): stocks with capacity bars and 4-season trend arrows;
// the projection for next season per resource (production minus consumption minus spoilage),
// live from the draft and greyed while recomputing, "assuming no events"; and the warning list.
// Every number carries a tooltip with its factors (M5a acceptance).
import type { ProjectionVM, StockBar, Trend, WarningLine } from '../../viewModels';
import { whole } from '../format';
import { resourceLabel, uiText } from '../text/en';
import { Num } from '../tooltip/num';
import { Tooltip } from '../tooltip/tooltip';
import { warningLine } from './text';

const ARROW: Record<Trend, string> = { up: '↑', down: '↓', flat: '→' };

function TrendArrow({ bar }: { bar: StockBar }) {
  const t = uiText.stocks;
  if (bar.direction === null || bar.trend === null)
    return <span class="stocks__trend hint">{bar.id === 'seed' ? '' : '·'}</span>;
  const word = bar.direction === 'up' ? t.trendUp : bar.direction === 'down' ? t.trendDown : t.trendFlat;
  return (
    <Tooltip
      class="stocks__trend"
      label={`${t.trend}: ${word}`}
      above
      content={() => (
        <span>
          {t.trend}: {bar.recent.map((v) => whole(v)).join(' → ')} ({word})
        </span>
      )}
    >
      <span aria-hidden="true">{ARROW[bar.direction]}</span>
      <span class="num"> {bar.trend > 0 ? '+' : ''}{whole(bar.trend)}</span>
    </Tooltip>
  );
}

function Stocks({ bars }: { bars: StockBar[] }) {
  return (
    <section class="panel" aria-labelledby="stocks-title">
      <h2 id="stocks-title" class="panel__title">
        {uiText.stocks.title}
      </h2>
      <ul class="stocks">
        {bars.map((b) => {
          const label = resourceLabel[b.id];
          return (
            <li key={b.id} class="stocks__row">
              <span class="stocks__label">{label}</span>
              <span class="stocks__value">
                <Num value={b.amount} factors={b.amountFactors} label={label} />
                {' / '}
                {b.capacityTarget === null ? (
                  <Num value={b.capacity} format="whole" />
                ) : (
                  <Num
                    value={b.capacity}
                    format="whole"
                    target={b.capacityTarget}
                    label={uiText.stocks.capacity(label)}
                  />
                )}
              </span>
              <TrendArrow bar={b} />
              <span class="bar" role="img" aria-label={`${Math.round(b.fill * 100)}%`}>
                <span
                  class={`bar__fill${b.fill > 1 ? ' is-over' : ''}`}
                  style={{ '--fill': `${Math.min(100, b.fill * 100)}%` }}
                />
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function Projection({ vm }: { vm: ProjectionVM }) {
  const t = uiText.projection;
  return (
    <section
      class={`panel projection${vm.pending ? ' is-pending' : ''}`}
      aria-labelledby="projection-title"
      aria-busy={vm.pending}
    >
      <h2 id="projection-title" class="panel__title">
        {t.title} <span class="hint">({t.assumption})</span>
        {vm.pending && <span class="hint projection__busy"> {t.recomputing}</span>}
      </h2>
      {vm.error !== null && <p class="hint">{t.failed}</p>}
      {!vm.available ? (
        <p class="hint">{t.unavailable}</p>
      ) : (
        <table class="table projection__table">
          <thead>
            <tr>
              <th scope="col">
                <span class="visually-hidden">Resource</span>
              </th>
              <th scope="col">{t.change}</th>
              <th scope="col">{t.end}</th>
            </tr>
          </thead>
          <tbody>
            {vm.rows.map((r) => (
              <tr key={r.id} class={r.short ? 'is-short' : ''}>
                <th scope="row">
                  {resourceLabel[r.id]}
                  {r.short && (
                    <span class="projection__short">
                      {' '}
                      <span aria-hidden="true">▽</span> {t.short}
                    </span>
                  )}
                </th>
                <td class="num">
                  <Num value={r.change} format="signed" factors={r.factors} label={resourceLabel[r.id]} above />
                </td>
                <td class="num">
                  <Num value={r.end} format="one" factors={r.endFactors} label={`${resourceLabel[r.id]} ${t.end}`} above />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function Warnings({ lines }: { lines: WarningLine[] }) {
  const t = uiText.warnings;
  return (
    <section class="panel" aria-labelledby="warnings-title">
      <h2 id="warnings-title" class="panel__title">
        {t.title}
      </h2>
      {lines.length === 0 ? (
        <p class="hint">{t.none}</p>
      ) : (
        <ul class="warnings">
          {lines.map((w, i) => (
            <li key={i} class={`warnings__item warnings__item--${w.source}`}>
              <span aria-hidden="true">{w.source === 'live' ? '△ ' : '· '}</span>
              {warningLine(w)} <span class="hint">({w.source === 'live' ? t.thisSeason : t.lastSeason})</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

interface Props {
  stocks: StockBar[];
  projection: ProjectionVM;
  warnings: WarningLine[];
}

export function RightPanel({ stocks, projection, warnings }: Props) {
  return (
    <aside class="side side--right" aria-label="Stocks and projection">
      <Stocks bars={stocks} />
      <Projection vm={projection} />
      <Warnings lines={warnings} />
    </aside>
  );
}
